import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { logout } from './authSlice';
import { setOrgWorkflowExtras } from '../../utils/reactionConfig';
import {
  deleteRealmDoc,
  queryRealm,
  readOrgWorkflowReactionsFromRealm,
  realmAvailable,
  upsertRealmDocs,
} from '../../realm';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const toCatalogItem = (row) => {
  if (!row) return null;
  const id = row.id || row._id;
  if (!id) return null;
  return {
    id,
    category: row.category,
    slug: row.slug,
    reactionId: row.reactionId || row.id,
    emoji: row.emoji,
    label: row.label,
    tone: row.tone,
    custom: row.custom !== false,
  };
};

const replaceRealmCatalog = async (list) => {
  if (!(await realmAvailable())) return;
  const keep = new Set(list.map((item) => String(item.id || item._id)));
  const existing = await queryRealm({ type: 'OrgWorkflowReaction' });
  await Promise.all((existing || []).map(async (row) => {
    const id = String(row.id || row._id || '');
    if (id && !keep.has(id)) {
      await deleteRealmDoc('OrgWorkflowReaction', id);
    }
  }));
  if (list.length) {
    await upsertRealmDocs('OrgWorkflowReaction', list);
  }
};

export const fetchOrgWorkflowReactions = createAsyncThunk(
  'workflowCatalog/fetch',
  async (_, { rejectWithValue }) => {
    const cached = ((await readOrgWorkflowReactionsFromRealm()) || [])
      .map(toCatalogItem)
      .filter(Boolean);

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return cached;
    }

    try {
      const response = await axios.get(`${API_URL}/org/workflow-reactions`, {
        headers: authHeaders(),
      });
      const list = (Array.isArray(response.data) ? response.data : [])
        .map(toCatalogItem)
        .filter(Boolean);
      await replaceRealmCatalog(list);
      return list;
    } catch (error) {
      if (cached.length) {
        return cached;
      }
      if (error?.response?.status === 403 || error?.response?.status === 400) {
        return [];
      }
      return rejectWithValue(error?.response?.data?.error || 'Failed to load workflow tags');
    }
  },
);

const applyItems = (items) => {
  const list = Array.isArray(items) ? items : [];
  setOrgWorkflowExtras(list);
  return list;
};

const workflowCatalogSlice = createSlice({
  name: 'workflowCatalog',
  initialState: {
    items: [],
    loaded: false,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrgWorkflowReactions.fulfilled, (state, action) => {
        state.items = applyItems(action.payload);
        state.loaded = true;
      })
      .addCase(fetchOrgWorkflowReactions.rejected, (state) => {
        state.items = applyItems([]);
        state.loaded = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.items = applyItems([]);
        state.loaded = false;
      });
  },
});

export default workflowCatalogSlice.reducer;
